/**
 * Remember `#app-main-scroll` position per route.
 *
 * The window is not the scroller — AppShell's <main> is — so the browser's
 * native scroll restoration never fires. `/onboarding/dashboard` and `/home/*`
 * also mount *separate* AppShell trees, so an in-component ref is lost on the
 * way to CRM. A module-level map survives both remounts for the life of the SPA.
 *
 * Two races this module must survive:
 *  1. Dashboard first paints a short "Loading…" shell — restoring 720px clamps
 *     to 0. A scroll listener that then saved 0 would wipe the real offset.
 *  2. CRM `/home/boards/leads` flips <main> to overflow-hidden (full-bleed),
 *     which zeros scrollTop BEFORE the previous effect's cleanup runs.
 */
const positions = new Map<string, number>();

export function mainScrollKey(pathname: string, search = ''): string {
  return `${pathname}${search || ''}`;
}

export function canHoldScroll(
  el: { scrollHeight: number; clientHeight: number } | null | undefined,
  top: number,
): boolean {
  if (!el || !(top > 0)) return true;
  return el.scrollHeight - el.clientHeight >= top - 1;
}

export function saveMainScroll(
  key: string,
  top: number,
  el?: { scrollHeight: number; clientHeight: number } | null,
): void {
  if (!key) return;
  const n = Number(top);
  const next = Number.isFinite(n) && n > 0 ? n : 0;
  const prev = positions.get(key);
  // Layout still too short to represent the saved offset (spinner / overflow
  // flip) — keep the real position instead of recording a clamped 0.
  if (prev != null && prev > next && el && !canHoldScroll(el, prev)) return;
  positions.set(key, next);
}

export function readMainScroll(key: string): number | undefined {
  return positions.get(key);
}

export function restoreMainScroll(
  el: { scrollTop: number; scrollHeight?: number; clientHeight?: number } | null | undefined,
  key: string,
): boolean {
  if (!el) return false;
  const saved = positions.get(key);
  if (saved == null) return false;
  const max =
    typeof el.scrollHeight === 'number' && typeof el.clientHeight === 'number'
      ? Math.max(0, el.scrollHeight - el.clientHeight)
      : saved;
  el.scrollTop = Math.min(saved, max);
  if (typeof window !== 'undefined' && saved > 0 && typeof window.scrollTo === 'function') {
    try {
      window.scrollTo(0, saved);
    } catch {
      /* jsdom */
    }
  }
  return true;
}

/** True once the scroller can actually sit at the saved offset (or nothing to restore). */
export function isMainScrollRestored(
  el: { scrollTop: number; scrollHeight: number; clientHeight: number } | null | undefined,
  key: string,
): boolean {
  const saved = positions.get(key);
  if (saved == null || saved === 0) return true;
  if (!el) return false;
  return canHoldScroll(el, saved) || el.scrollTop >= saved - 1;
}

/** Capture now — call before navigate() so a full-bleed destination cannot wipe it. */
export function snapshotMainScroll(pathname: string, search = ''): void {
  if (typeof document === 'undefined') return;
  const main = document.getElementById('app-main-scroll');
  const mainTop = main?.scrollTop ?? 0;
  const winTop = window.scrollY || document.documentElement.scrollTop || 0;
  saveMainScroll(mainScrollKey(pathname, search), Math.max(mainTop, winTop), main);
}

/** Test-only — the map is process-lifetime otherwise. */
export function resetMainScrollPositions(): void {
  positions.clear();
}
